/**
 * Salary Statistics Chart Component
 * Displays salary distribution and compares job salary range to market statistics
 */

import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isValidNumber, isPositiveNumber } from '../utils/numberUtils';

interface SalaryStatistics {
  median: { min: number; max: number };
  average: { min: number; max: number };
  min: number;
  max: number;
  percentile25: { min: number; max: number };
  percentile75: { min: number; max: number };
  percentile90: { min: number; max: number };
  sampleSize: number;
  aggregationType?: string;
  dimensionValue?: string;
}

interface SalaryStatisticsChartProps {
  salaryStats?: SalaryStatistics;
  locationSalaryStats?: Record<string, SalaryStatistics>; // Fallback: location-only stats
  jobSalaryMin?: number;
  jobSalaryMax?: number;
  currency?: string;
  salaryPeriod?: 'annual' | 'monthly' | 'daily' | 'hourly';
  locale?: string;
  className?: string;
  jobLocation?: string[]; // Job locations for fallback
}

export const SalaryStatisticsChart: React.FC<SalaryStatisticsChartProps> = memo(({
  salaryStats,
  locationSalaryStats,
  jobSalaryMin,
  jobSalaryMax,
  currency = '€',
  salaryPeriod = 'annual',
  locale = 'en',
  className = '',
  jobLocation = [],
}) => {
  const intl = useIntl();
  
  // Helper function to safely get sampleSize with validation
  const getValidSampleSize = (stats: SalaryStatistics | undefined | null): number => {
    if (!stats) return 0;
    const size = stats.sampleSize;
    return isValidNumber(size) && size >= 0 ? size : 0;
  };
  
  // Determine which stats to use and their accuracy level
  let effectiveStats: SalaryStatistics | null = null;
  let accuracyLevel: 'exact' | 'location' | 'general' = 'general';
  let accuracyDescription: string = '';
  
  if (salaryStats && getValidSampleSize(salaryStats) > 0) {
    effectiveStats = salaryStats;
    // Determine accuracy based on aggregationType
    if (salaryStats.aggregationType?.includes('COMPANY') && salaryStats.aggregationType.includes('TITLE')) {
      accuracyLevel = 'exact';
      accuracyDescription = intl.formatMessage({ id: 'salaryStats.accuracy.exact', defaultMessage: 'Based on similar roles at this company', description: 'Accuracy description when salary stats are based on same company data' });
    } else if (salaryStats.aggregationType?.includes('LOCATION')) {
      accuracyLevel = 'location';
      const locationName = salaryStats.dimensionValue?.split('|').find(v => jobLocation.includes(v)) || jobLocation[0] || '';
      accuracyDescription = intl.formatMessage(
        { id: 'salaryStats.accuracy.location', defaultMessage: 'Based on salaries in {location}', description: 'Accuracy description when salary stats are based on location data, where {location} is the city/region' },
        { location: locationName }
      );
    } else {
      accuracyLevel = 'general';
      accuracyDescription = intl.formatMessage({ id: 'salaryStats.accuracy.general', defaultMessage: 'Based on market data', description: 'Accuracy description when salary stats are based on general market data' });
    }
  } else if (locationSalaryStats && jobLocation.length > 0) {
    // Try to find location-only stats as fallback
    for (const location of jobLocation) {
      const locationStats = locationSalaryStats[location];
      if (locationStats && getValidSampleSize(locationStats) > 0) {
        effectiveStats = locationStats;
        accuracyLevel = 'location';
        accuracyDescription = intl.formatMessage(
          { id: 'salaryStats.accuracy.location', defaultMessage: 'Based on salaries in {location}', description: 'Accuracy description when salary stats are based on location data, where {location} is the city/region' },
          { location }
        );
        break;
      }
    }
  }
  
  if (!effectiveStats || getValidSampleSize(effectiveStats) === 0) {
    return null;
  }
  
  const sampleSize = getValidSampleSize(effectiveStats);
  const hasSmallSampleSize = sampleSize < 3;
  
  // Use effectiveStats for the rest of the component
  const stats = effectiveStats;
  
  
  const hasJobSalary = isPositiveNumber(jobSalaryMin) && isPositiveNumber(jobSalaryMax);
  
  // Normalize all values to monthly for consistent comparison
  const normalizeToMonthly = (value: number, period: string): number => {
    if (!isValidNumber(value) || value <= 0) {
      return 0;
    }
    let result: number;
    switch (period) {
      case 'annual': result = value / 12; break;
      case 'monthly': result = value; break;
      case 'daily': result = value * 22; break; // Approximate working days per month
      case 'hourly': result = value * 22 * 8; break; // Approximate working hours per month
      default: result = value;
    }
    return isValidNumber(result) && result > 0 ? result : 0;
  };
  
  const formatPeriod = (period: string): string => {
    switch (period) {
      case 'annual': return intl.formatMessage({ id: 'salaryStats.perYear', defaultMessage: 'per year', description: 'Time period unit for annual salary' });
      case 'monthly': return intl.formatMessage({ id: 'salaryStats.perMonth', defaultMessage: 'per month', description: 'Time period unit for monthly salary' });
      case 'daily': return intl.formatMessage({ id: 'salaryStats.perDay', defaultMessage: 'per day', description: 'Time period unit for daily salary' });
      case 'hourly': return intl.formatMessage({ id: 'salaryStats.perHour', defaultMessage: 'per hour', description: 'Time period unit for hourly salary' });
      default: return '';
    }
  };
  
  // Chart dimensions
  const width = 600;
  const height = 200;
  const padding = { top: 20, right: 40, bottom: 40, left: 80 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  // Salary stats are already normalized to monthly EUR, so use them directly
  // Ensure values are numbers and handle edge cases with proper validation
  const statsMin = isPositiveNumber(stats.min) ? stats.min : null;
  const statsMax = isPositiveNumber(stats.max) ? stats.max : null;
  const medianMin = stats.median && isPositiveNumber(stats.median.min) ? stats.median.min : null;
  const medianMax = stats.median && isPositiveNumber(stats.median.max) ? stats.median.max : null;
  const p25Min = stats.percentile25 && isPositiveNumber(stats.percentile25.min) ? stats.percentile25.min : null;
  const p25Max = stats.percentile25 && isPositiveNumber(stats.percentile25.max) ? stats.percentile25.max : null;
  const p75Min = stats.percentile75 && isPositiveNumber(stats.percentile75.min) ? stats.percentile75.min : null;
  const p75Max = stats.percentile75 && isPositiveNumber(stats.percentile75.max) ? stats.percentile75.max : null;
  const avgMin = stats.average && isPositiveNumber(stats.average.min) ? stats.average.min : null;
  const avgMax = stats.average && isPositiveNumber(stats.average.max) ? stats.average.max : null;
  
  // Determine the actual range from available data (use median if min/max are not available)
  const effectiveMin = statsMin ?? p25Min ?? medianMin ?? avgMin ?? 0;
  const effectiveMax = statsMax ?? p75Max ?? medianMax ?? avgMax ?? 0;
  
  // Helper function to safely get median value for display
  const getMedianValue = (): number => {
    if (medianMin !== null && medianMax !== null && isValidNumber(medianMin) && isValidNumber(medianMax)) {
      const result = (medianMin + medianMax) / 2;
      return isValidNumber(result) ? result : 0;
    }
    if (medianMin !== null && isValidNumber(medianMin)) return medianMin;
    if (medianMax !== null && isValidNumber(medianMax)) return medianMax;
    // Fallback to effective range midpoint
    if (isValidNumber(effectiveMin) && isValidNumber(effectiveMax)) {
      const result = (effectiveMin + effectiveMax) / 2;
      return isValidNumber(result) ? result : 0;
    }
    return 0;
  };
  
  // Check if we have any valid non-zero values
  const hasValidData = effectiveMin > 0 || effectiveMax > 0;
  
  // If stats are all zeros or invalid, show a message instead of hiding
  if (!hasValidData || !effectiveStats) {
    return (
      <div className={`salary-statistics-chart ${className} mt-4`}>
        <div className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
          <FormattedMessage
            id="jobDetailPage.salaryStatistics"
            defaultMessage="Salary Statistics"
            description="Section heading for salary statistics and market comparison chart"
          />
          {sampleSize > 0 && (
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">
              ({sampleSize} {intl.formatMessage({ id: 'salaryStats.samples', defaultMessage: 'samples', description: 'Label for sample count in salary statistics (e.g., "50 samples")' })})
            </span>
          )}
          {accuracyLevel !== 'exact' && accuracyDescription && (
            <div className="text-xs font-normal text-amber-600 dark:text-amber-400 mt-1 italic">
              {accuracyDescription}
            </div>
          )}
          {hasSmallSampleSize && (
            <div className="text-xs font-normal text-amber-600 dark:text-amber-400 mt-1 italic">
              <FormattedMessage
                id="salaryStats.smallSampleWarning"
                defaultMessage="Note: Based on a small sample size ({sampleSize} {samples}). Results may not be representative."
                description="Warning message when salary statistics are based on a small number of samples, where {sampleSize} is the count and {samples} is the pluralized label"
                values={{
                  sampleSize: sampleSize,
                  samples: intl.formatMessage({ id: 'salaryStats.samples', defaultMessage: 'samples', description: 'Label for sample count in salary statistics (e.g., "50 samples")' }),
                }}
              />
            </div>
          )}
        </div>
        <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl text-sm text-gray-600 dark:text-gray-400">
          <FormattedMessage
            id="salaryStats.noValidValues"
            defaultMessage="Salary statistics are available ({sampleSize} {samples}) but all values are zero. This may indicate the data needs to be regenerated."
            description="Error message when salary statistics exist but contain only zero values, where {sampleSize} is the count and {samples} is the pluralized label"
            values={{
              sampleSize: sampleSize,
              samples: intl.formatMessage({ id: 'salaryStats.samples', defaultMessage: 'samples' }),
            }}
          />
        </div>
      </div>
    );
  }
  
  // Use effective values for chart range
  const chartStatsMin = statsMin ?? effectiveMin;
  const chartStatsMax = statsMax ?? effectiveMax;
  
  // Normalize job salary to monthly for comparison (stats are already monthly)
  const jobMinMonthly = hasJobSalary && isValidNumber(jobSalaryMin) ? normalizeToMonthly(jobSalaryMin, salaryPeriod) : null;
  const jobMaxMonthly = hasJobSalary && isValidNumber(jobSalaryMax) ? normalizeToMonthly(jobSalaryMax, salaryPeriod) : null;
  
  const chartMin = Math.min(
    isValidNumber(chartStatsMin) ? chartStatsMin : 0,
    isValidNumber(jobMinMonthly) ? jobMinMonthly : (isValidNumber(chartStatsMin) ? chartStatsMin : 0)
  );
  const chartMax = Math.max(
    isValidNumber(chartStatsMax) ? chartStatsMax : 0,
    isValidNumber(jobMaxMonthly) ? jobMaxMonthly : (isValidNumber(chartStatsMax) ? chartStatsMax : 0)
  );
  const range = chartMax - chartMin;
  
  // If range is 0 or invalid, don't render
  if (!isValidNumber(range) || range <= 0) {
    return null;
  }
  
  // Scale function with validation
  const scaleX = (value: number) => {
    if (!isValidNumber(value) || !isValidNumber(chartMin) || !isValidNumber(range)) {
      return padding.left;
    }
    const scaled = padding.left + ((value - chartMin) / range) * chartWidth;
    return isValidNumber(scaled) ? scaled : padding.left;
  };
  
  // Calculate positions for percentiles (use validated values or fallback to effective range)
  const p25X = scaleX(p25Min ?? effectiveMin);
  const p25Width = (p25Max ?? effectiveMax) - (p25Min ?? effectiveMin);
  const medianX = scaleX(medianMin ?? effectiveMin);
  const medianWidth = (medianMax ?? effectiveMax) - (medianMin ?? effectiveMin);
  const p75X = scaleX(p75Min ?? effectiveMin);
  const p75Width = (p75Max ?? effectiveMax) - (p75Min ?? effectiveMin);
  const avgX = scaleX(avgMin ?? effectiveMin);
  const avgWidth = (avgMax ?? effectiveMax) - (avgMin ?? effectiveMin);
  
  // Job salary position
  const jobMinX = jobMinMonthly ? scaleX(jobMinMonthly) : null;
  const jobMaxX = jobMaxMonthly ? scaleX(jobMaxMonthly) : null;
  const jobWidth = jobMinX && jobMaxX ? jobMaxX - jobMinX : null;
  
  // Determine job salary position relative to market
  const getJobPosition = (): 'below' | 'at' | 'above' | null => {
    if (!hasJobSalary || !isValidNumber(jobMinMonthly) || !isValidNumber(jobMaxMonthly)) {
      return null;
    }
    
    const jobMid = (jobMinMonthly + jobMaxMonthly) / 2;
    if (!isValidNumber(jobMid)) return null;
    
    const medianMidMin = isValidNumber(medianMin) ? medianMin : (isValidNumber(effectiveMin) ? effectiveMin : 0);
    const medianMidMax = isValidNumber(medianMax) ? medianMax : (isValidNumber(effectiveMax) ? effectiveMax : 0);
    const medianMid = (medianMidMin + medianMidMax) / 2;
    
    const p25Value = isValidNumber(p25Min) ? p25Min : (isValidNumber(effectiveMin) ? effectiveMin : 0);
    const p75Value = isValidNumber(p75Max) ? p75Max : (isValidNumber(effectiveMax) ? effectiveMax : 0);
    
    if (!isValidNumber(p25Value) || !isValidNumber(p75Value)) return null;
    
    if (jobMid < p25Value) return 'below';
    if (jobMid > p75Value) return 'above';
    return 'at';
  };
  
  const jobPosition = getJobPosition();
  
  // Format currency value with validation
  const formatCurrency = (value: number, showPeriod = false): string => {
    if (!isValidNumber(value) || value <= 0) {
      return currency + '0';
    }
    const rounded = Math.round(value);
    if (!isValidNumber(rounded)) {
      return currency + '0';
    }
    const formatted = `${currency}${rounded.toLocaleString(locale)}`;
    return showPeriod ? `${formatted} ${formatPeriod(salaryPeriod)}` : formatted;
  };
  
  return (
    <div className={`salary-statistics-chart ${className} mt-4`}>
      <div className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
        <FormattedMessage
          id="jobDetailPage.salaryStatistics"
          defaultMessage="Salary Statistics"
        />
        {sampleSize > 0 && (
          <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-2">
            ({sampleSize} {intl.formatMessage({ id: 'salaryStats.samples', defaultMessage: 'samples' })})
          </span>
        )}
      </div>
      
      {/* Chart */}
      <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl">
        <svg width={width} height={height} className="w-full max-w-full">
          {/* Background */}
          <rect
            x={padding.left}
            y={padding.top}
            width={chartWidth}
            height={chartHeight}
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            className="text-gray-200 dark:text-gray-600"
          />
          
          {/* Percentile ranges */}
          {/* 25th percentile */}
          <rect
            x={p25X}
            y={padding.top + chartHeight * 0.3}
            width={p25Width}
            height={chartHeight * 0.2}
            fill="#fef3c7"
            opacity="0.6"
            className="dark:fill-yellow-900/30"
          />
          
          {/* Median (50th percentile) */}
          <rect
            x={medianX}
            y={padding.top + chartHeight * 0.2}
            width={medianWidth}
            height={chartHeight * 0.6}
            fill="#dbeafe"
            opacity="0.7"
            className="dark:fill-blue-900/40"
          />
          
          {/* 75th percentile */}
          <rect
            x={p75X}
            y={padding.top + chartHeight * 0.3}
            width={p75Width}
            height={chartHeight * 0.2}
            fill="#fef3c7"
            opacity="0.6"
            className="dark:fill-yellow-900/30"
          />
          
          {/* Average */}
          <rect
            x={avgX}
            y={padding.top + chartHeight * 0.1}
            width={avgWidth}
            height={chartHeight * 0.8}
            fill="#f3f4f6"
            opacity="0.5"
            stroke="#6b7280"
            strokeWidth="1"
            strokeDasharray="4 2"
            className="dark:fill-gray-800 dark:stroke-gray-500"
          />
          
          {/* Job salary range (if present) */}
          {hasJobSalary && jobMinX && jobMaxX && jobWidth && (
            <g>
              <rect
                x={jobMinX}
                y={padding.top + chartHeight * 0.05}
                width={jobWidth}
                height={chartHeight * 0.9}
                fill="#10b981"
                opacity="0.8"
                stroke="#059669"
                strokeWidth="2"
                className="dark:fill-green-600 dark:stroke-green-500"
              />
              {/* Job salary labels */}
              <text
                x={jobMinX}
                y={padding.top - 5}
                textAnchor="middle"
                className="text-xs fill-green-700 dark:fill-green-400 font-semibold"
                fontSize="10"
              >
                {formatCurrency(jobSalaryMin)}
              </text>
              <text
                x={jobMaxX}
                y={padding.top - 5}
                textAnchor="middle"
                className="text-xs fill-green-700 dark:fill-green-400 font-semibold"
                fontSize="10"
              >
                {formatCurrency(jobSalaryMax)}
              </text>
            </g>
          )}
          
          {/* Min/Max markers */}
          {chartStatsMin > 0 && (
            <line
              x1={scaleX(chartStatsMin)}
              x2={scaleX(chartStatsMin)}
              y1={padding.top}
              y2={padding.top + chartHeight}
              stroke="currentColor"
              strokeWidth="1"
              strokeDasharray="2 2"
              className="text-gray-400 dark:text-gray-500"
            />
          )}
          {chartStatsMax > 0 && (
            <line
              x1={scaleX(chartStatsMax)}
              x2={scaleX(chartStatsMax)}
              y1={padding.top}
              y2={padding.top + chartHeight}
              stroke="currentColor"
              strokeWidth="1"
              strokeDasharray="2 2"
              className="text-gray-400 dark:text-gray-500"
            />
          )}
          
          {/* Labels */}
          {chartStatsMin > 0 && (
            <text
              x={scaleX(chartStatsMin)}
              y={height - 5}
              textAnchor="middle"
              className="text-xs fill-gray-600 dark:fill-gray-300"
              fontSize="9"
            >
              {formatCurrency(chartStatsMin)}
            </text>
          )}
          {chartStatsMax > 0 && (
            <text
              x={scaleX(chartStatsMax)}
              y={height - 5}
              textAnchor="middle"
              className="text-xs fill-gray-600 dark:fill-gray-300"
              fontSize="9"
            >
              {formatCurrency(chartStatsMax)}
            </text>
          )}
          
          {/* Legend */}
          <g transform={`translate(${padding.left}, ${padding.top + chartHeight + 20})`}>
            <rect x="0" y="0" width="12" height="12" fill="#dbeafe" opacity="0.7" className="dark:fill-blue-900/40" />
<text x="18" y="9" className="text-xs fill-gray-700 dark:fill-gray-200" fontSize="9">
            <FormattedMessage id="salaryStats.median" defaultMessage="Median" description="Legend label for median salary value in chart" />
            </text>
            
            <rect x="80" y="0" width="12" height="12" fill="#fef3c7" opacity="0.6" className="dark:fill-yellow-900/30" />
            <text x="98" y="9" className="text-xs fill-gray-700 dark:fill-gray-200" fontSize="9">
              <FormattedMessage id="salaryStats.percentiles" defaultMessage="25th-75th percentile" description="Legend label for 25th to 75th percentile salary range in chart" />
            </text>
            
            <rect x="220" y="0" width="12" height="12" fill="#f3f4f6" opacity="0.5" stroke="#6b7280" strokeWidth="1" strokeDasharray="4 2" className="dark:fill-gray-800 dark:stroke-gray-500" />
            <text x="238" y="9" className="text-xs fill-gray-700 dark:fill-gray-200" fontSize="9">
              <FormattedMessage id="salaryStats.average" defaultMessage="Average" description="Legend label for average salary value in chart" />
            </text>
            
            {hasJobSalary && (
              <>
                <rect x="320" y="0" width="12" height="12" fill="#10b981" opacity="0.8" className="dark:fill-green-600" />
                <text x="338" y="9" className="text-xs fill-gray-700 dark:fill-gray-200" fontSize="9">
                  <FormattedMessage id="salaryStats.jobSalary" defaultMessage="This job" description="Legend label for the specific job offer salary in chart" />
                </text>
              </>
            )}
          </g>
        </svg>
      </div>
      
      {/* Summary text */}
      <div className="mt-3 text-sm text-gray-700 dark:text-gray-300 space-y-1">
        {hasJobSalary && jobPosition && (
          <div className="font-medium">
            {jobPosition === 'below' && (
              <FormattedMessage
                id="salaryStats.belowMarket"
                defaultMessage="This salary is below the market median"
                description="Message indicating the job salary is lower than the market median"
              />
            )}
            {jobPosition === 'at' && (
              <FormattedMessage
                id="salaryStats.atMarket"
                defaultMessage="This salary is competitive with the market median"
                description="Message indicating the job salary is competitive with the market median"
              />
            )}
            {jobPosition === 'above' && (
              <FormattedMessage
                id="salaryStats.aboveMarket"
                defaultMessage="This salary is above the market median"
                description="Message indicating the job salary is higher than the market median"
              />
            )}
          </div>
        )}
        <div>
          <FormattedMessage
            id="salaryStats.marketRange"
            defaultMessage="Market range: {min} - {max} (median: {median})"
            description="Text showing market salary range and median, where {min}, {max}, and {median} are currency values"
            values={{
              min: formatCurrency(chartStatsMin),
              max: formatCurrency(chartStatsMax),
              median: formatCurrency(getMedianValue()),
            }}
          />
        </div>
        {!hasJobSalary && (
          <div className="text-xs text-gray-500 dark:text-gray-400 italic">
            <FormattedMessage
              id="salaryStats.noJobSalary"
              defaultMessage="No salary range provided for this job. Chart shows market distribution."
              description="Message explaining that the job does not have a salary range, so only market data is shown"
            />
          </div>
        )}
      </div>
    </div>
  );
});

SalaryStatisticsChart.displayName = 'SalaryStatisticsChart';

